import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Building2, Package, PlusCircle, Eye, MousePointer } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { PartnerProfileForm } from "@/components/partner/PartnerProfileForm";
import { CreateOfferDialog } from "@/components/partner/CreateOfferDialog";
import { OfferCard } from "@/components/partner/OfferCard";

interface PartnerProfile {
  id: string;
  company_name: string;
  company_description: string | null;
  website_url: string | null;
  logo_url: string | null;
  contact_email: string;
  contact_phone: string | null;
  is_verified: boolean;
}

interface PartnerOffer {
  id: string;
  title: string;
  description: string;
  offer_type: string;
  image_url: string | null;
  price: number | null;
  discount_percentage: number | null;
  status: string;
  is_active: boolean;
  view_count: number;
  click_count: number;
  created_at: string;
}

const DashboardPartner = () => {
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    fetchPartnerData();
  }, []);

  const fetchPartnerData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch partner profile
      const { data: profileData, error: profileError } = await supabase
        .from("partner_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setProfile(profileData);

      // Fetch offers if profile exists
      if (profileData) {
        const { data: offersData, error: offersError } = await supabase
          .from("partner_offers")
          .select("*")
          .eq("partner_id", profileData.id)
          .order("created_at", { ascending: false });

        if (offersError) throw offersError;
        setOffers(offersData || []);
      }
    } catch (error) {
      console.error("Error fetching partner data:", error);
      toast.error("Failed to load partner dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleOfferCreated = () => {
    fetchPartnerData();
    setIsCreateDialogOpen(false);
  };

  const handleOfferUpdated = () => {
    fetchPartnerData();
  };

  const handleOfferDeleted = () => {
    fetchPartnerData();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const totalViews = offers.reduce((sum, offer) => sum + offer.view_count, 0);
  const totalClicks = offers.reduce((sum, offer) => sum + offer.click_count, 0);
  const approvedOffers = offers.filter(o => o.status === "approved").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-4xl font-bold">Partner Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Manage your company profile and offers
            </p>
          </div>
        </div>

        {!profile ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Welcome Partner!
              </CardTitle>
              <CardDescription>
                Create your partner profile to start uploading offers and co-trainings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PartnerProfileForm onSuccess={fetchPartnerData} />
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Offers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{offers.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Approved Offers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{approvedOffers}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Total Views
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalViews}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <MousePointer className="h-4 w-4" />
                    Total Clicks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{totalClicks}</div>
                </CardContent>
              </Card>
            </div>

            {/* Profile Status */}
            {!profile.is_verified && (
              <Card className="mb-6 border-yellow-500/50 bg-yellow-500/10">
                <CardContent className="pt-6">
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    Your profile is pending verification. You can create offers, but they won't be visible until your profile is verified by an administrator.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Main Content Tabs */}
            <Tabs defaultValue="offers" className="space-y-6">
              <TabsList>
                <TabsTrigger value="offers" className="gap-2">
                  <Package className="h-4 w-4" />
                  My Offers
                </TabsTrigger>
                <TabsTrigger value="profile" className="gap-2">
                  <Building2 className="h-4 w-4" />
                  Company Profile
                </TabsTrigger>
              </TabsList>

              <TabsContent value="offers" className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">Your Offers</h2>
                    <p className="text-muted-foreground">
                      Manage your training offers and collaborations
                    </p>
                  </div>
                  <CreateOfferDialog
                    partnerId={profile.id}
                    open={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onSuccess={handleOfferCreated}
                  >
                    <Button className="gap-2">
                      <PlusCircle className="h-4 w-4" />
                      Create Offer
                    </Button>
                  </CreateOfferDialog>
                </div>

                {offers.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Package className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No offers yet</h3>
                      <p className="text-muted-foreground text-center mb-4">
                        Create your first offer to start collaborating
                      </p>
                      <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                        <PlusCircle className="h-4 w-4" />
                        Create Your First Offer
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {offers.map((offer) => (
                      <OfferCard
                        key={offer.id}
                        offer={offer}
                        onUpdate={handleOfferUpdated}
                        onDelete={handleOfferDeleted}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="profile">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Company Profile
                      {profile.is_verified && (
                        <Badge className="ml-2 bg-green-500">Verified</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Update your company information and branding
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PartnerProfileForm
                      initialData={profile}
                      onSuccess={fetchPartnerData}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPartner;
